import { ref, watch, onMounted, shallowRef } from 'vue';
import { useChartTheme } from '@rdrive/theme';
import { downloadChartAsPNG } from '@rdrive/helper-chart-export';
import { axisChartOption } from '../../helpers/axisChartOption.js';

export default (props) => {
  // THE colour source — no `chartFontColor` prop. A theme toggle re-evaluates this
  // computed and the watch below repaints, without the page reloading anything.
  const theme = useChartTheme();

  const chartRef = ref(null);
  // shallowRef, not ref: the option goes straight into ECharts, and a deep ref
  // would hand it reactive proxies of every data point. Nothing watches it —
  // `updateChartOption()` assigns it explicitly.
  const chartOption = shallowRef({});

  // `colorBy: 'series'` → one colour for the whole series. `colorBy: 'bar'` → bar i
  // takes series[i % length], as an itemStyle.color FUNCTION of params.dataIndex,
  // exactly as DocumentFileTypeBarChart.vue did with its inlined palette.
  const seriesColor = (index) => {
    const palette = theme.value.series;
    return palette[((index % palette.length) + palette.length) % palette.length];
  };

  const isSet = (value) => value !== null && value !== undefined;

  // The series data: plain numbers — unless a row carries its own `color` or a row is selected
  // (opt-ins), when each bar becomes a data item with its own itemStyle (ECharts reads an item's
  // `itemStyle` over the series'): the row's colour, and — while `selectedId` is set — opacity 1
  // for the selected row and the theme's `dimOpacity` for every other one.
  const seriesData = (rows) => {
    const selecting = isSet(props.selectedId);
    if (!selecting && !rows.some((row) => isSet(row.color))) return rows.map((row) => row.value);
    return rows.map((row) => {
      const style = {};
      if (isSet(row.color)) style.color = row.color;
      if (selecting) style.opacity = row.id === props.selectedId ? 1 : theme.value.dimOpacity;
      return Object.keys(style).length > 0 ? { value: row.value, itemStyle: style } : { value: row.value };
    });
  };

  const updateChartOption = () => {
    const horizontal = props.orientation === 'horizontal';
    const rows = props.rows ?? [];
    const fontColor = theme.value.fontColor;

    const base = axisChartOption({
      theme: theme.value,
      orientation: props.orientation,
      categories: rows.map((row) => row.label),
      formatValue: props.formatValue,
      labelRotate: props.labelRotate,
      valueAxisName: props.valueAxisName,
      valueMin: props.valueMin,
      valueMax: props.valueMax,
      valueMinInterval: props.valueMinInterval,
      labelInterval: props.labelInterval,
    });

    const radius = props.borderRadius;
    const itemStyle = {
      borderRadius: horizontal ? [0, radius, radius, 0] : [radius, radius, 0, 0],
    };
    if (props.colorBy === 'bar') {
      itemStyle.color = (params) => seriesColor(params.dataIndex);
    } else {
      itemStyle.color = seriesColor(props.colorIndex);
    }

    const series = {
      name: props.seriesName,
      type: 'bar',
      data: seriesData(rows),
      itemStyle,
      label: {
        show: props.showLabels,
        position: horizontal ? 'right' : 'top',
        formatter: (params) =>
          props.formatLabel ? props.formatLabel(params.value, rows[params.dataIndex]) : props.formatValue(params.value),
        color: fontColor,
      },
    };
    if (isSet(props.barMaxWidth)) series.barMaxWidth = props.barMaxWidth;

    const option = {
      ...base,
      tooltip: {
        ...base.tooltip,
        axisPointer: { type: 'shadow' },
        formatter: (params) => {
          const point = Array.isArray(params) ? params[0] : params;
          if (!point) return '';
          if (props.formatTooltip) return props.formatTooltip(rows[point.dataIndex], point);
          const label = point.axisValue ?? rows[point.dataIndex]?.label ?? '';
          const prefix = props.seriesName ? `${props.seriesName}: ` : '';
          return `<strong>${label}</strong><br/>${prefix}${props.formatValue(point.value)}`;
        },
      },
      series: [series],
    };
    // Opt-in (asset-management): room for a horizontal bar's value label beyond the plot's right
    // edge — the grid key keeps its place, only `right` changes
    if (isSet(props.gridRight)) option.grid = { ...base.grid, right: props.gridRight };
    chartOption.value = option;
  };

  // Rows BY REFERENCE (no deep watch): the app adapter recomputes the whole array.
  // `formatValue` is deliberately not watched — it only ever changes together with
  // the rows it formats (a metric toggle swaps both); nor are the opt-in `formatLabel` /
  // `formatTooltip`, for the same reason. The selection and the axis/bar opt-ins are.
  watch(
    [
      () => props.rows,
      () => props.orientation,
      () => props.colorBy,
      () => props.colorIndex,
      () => props.showLabels,
      () => props.labelRotate,
      () => props.valueAxisName,
      () => props.seriesName,
      () => props.borderRadius,
      () => props.selectedId,
      () => props.valueMin,
      () => props.valueMax,
      () => props.valueMinInterval,
      () => props.barMaxWidth,
      () => props.labelInterval,
      () => props.gridRight,
      theme,
    ],
    updateChartOption,
  );

  onMounted(updateChartOption);

  const handleClick = (params) => {
    if (!props.onSelect) return;
    props.onSelect(props.rows?.[params?.dataIndex]);
  };

  // Opt-in PNG export, called by the page (template ref) — no legend: the bars carry their labels
  const downloadPng = () => {
    const chart = chartRef.value?.chart;
    if (!chart || !props.exportFilename) return;
    downloadChartAsPNG(chart, props.exportFilename, null, {
      mode: theme.value.mode,
      legend: false,
      title: props.exportTitle,
    });
  };

  return {
    // `chartRef` is a STRING template ref (`ref="chartRef"` in component.vue) — it
    // only binds because it is returned under that exact name.
    chartRef,
    chartOption,
    handleClick,
    downloadPng,
  };
};
